// tslint:disable-next-line:max-line-length
import { IDoctor, IHospital, INursingHome, INursingService, IPharmacy } from '@alberta/konexi-shared';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { LoadingController, ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { splitMultipleEmails } from 'src/app/common/email/email-helper';
import { multiValidateEmail } from 'src/app/common/email/multi-email-validator';
import { InstitutionsService } from 'src/app/institution/master/institutions.service';
import { SelectionItem } from 'src/app/shared/models/selectionItem';
import { InstitutionContactService } from 'src/app/shared/services/institution-contact.service';
import { PatientContactService, PatientContactServiceTypes } from 'src/app/shared/services/patient-contact.service';
import { SubunitService } from 'src/app/shared/services/subunit.service';

@Component({
  selector: 'document-email-modal',
  templateUrl: 'document-email.modal.html',
})
export class DocumentEmailModal implements OnInit {
  public yesNoOptions: SelectionItem[] = [];
  public institution: IHospital | INursingHome | INursingService | IPharmacy | IDoctor;
  public patientId: string;
  public institutionType: string;
  public contactSelectionItems: SelectionItem[] = [];
  public form: UntypedFormGroup;
  public config: { allowIndividualMail: boolean } = { allowIndividualMail: true };

  constructor(
    private modalCtrl: ModalController,
    private _translate: TranslateService,
    private _cdr: ChangeDetectorRef,
    private _loadingCtrl: LoadingController,
    private _patientContactService: PatientContactService,
    private _formBuilder: UntypedFormBuilder,
    private _institutionContactService: InstitutionContactService,
    private _institutionsService: InstitutionsService,
    private _subUnitService: SubunitService
  ) {
    this.yesNoOptions.push(
      new SelectionItem(true, this._translate.instant('components.swodoc.email-on-complete.modal-send'))
    );
    this.yesNoOptions.push(new SelectionItem(false, this._translate.instant('CANCEL_BUTTON')));
    this.form = this.getForm(this._formBuilder);
  }

  public get emailSelectionDisabled(): boolean {
    return !this.contactSelectionItems || !this.contactSelectionItems.length;
  }

  async ngOnInit() {
    const loading = await this._loadingCtrl.create({
      message: this._translate.instant('loadingDataText'),
    });
    await loading.present();
    try {
      await this.getContactSelectionItems();
    } catch (error) {
      window.logger.error('loading of patientContacts failed', error);
    } finally {
      this._cdr.detectChanges();
      await loading.dismiss();
    }
  }

  private async getContactSelectionItems() {
    if (this.institution) {
      await this.getInstitutionContacts();
    } else {
      await this.getPatientContacts();
    }
  }

  private async getInstitutionContacts(): Promise<void> {
    if (this.institution.email) {
      const type = this.institutionType ? this.institutionType : this._institutionsService.getInstitutionType();
      const name =
        type === 'Doctor'
          ? `${(this.institution as IDoctor).firstName} ${(this.institution as IDoctor).lastName}`
          : (this.institution as any).name;

      const institutionEmail = new SelectionItem(
        { institutionId: this.institution._id, email: this.institution.email },
        name
      );
      this.contactSelectionItems.push(institutionEmail);
    }
    const institutionContacts = await this._institutionContactService.loadInstitutionContacts(this.institution._id);
    if (institutionContacts?.length) {
      const selectionItems = institutionContacts
        .filter(contact => contact.email)
        .map(
          contact =>
            new SelectionItem(
              { institutionId: this.institution._id, email: contact.email },
              `${contact.firstName} ${contact.lastName}`
            )
        );
      this.contactSelectionItems.push(...selectionItems);
    }
    const subunits = await this._subUnitService.loadSubunits(this.institution._id);
    if (subunits?.length) {
      const subunitSelectionItems = subunits
        .filter(subunit => subunit.email)
        .map(subunit => new SelectionItem({ institutionId: this.institution._id, email: subunit.email }, subunit.name));
      this.contactSelectionItems.push(...subunitSelectionItems);
    }
  }

  private async getPatientContacts(): Promise<void> {
    const patientContacts = await this._patientContactService.getPatientContacts(this.patientId, [
      PatientContactServiceTypes.patient,
      PatientContactServiceTypes.caregiver,
      PatientContactServiceTypes.nursingHome,
    ]);
    this.contactSelectionItems = patientContacts
      .filter(contact => contact.email)
      .map(
        contact =>
          new SelectionItem(
            { institutionId: contact.institutionId, email: contact.email },
            `${contact.name} (${contact.type})`
          )
      );
  }

  private getForm(formBuilder: UntypedFormBuilder): UntypedFormGroup {
    return formBuilder.group({
      selectedContacts: [[]],
      individualMail: ['', multiValidateEmail],
    });
  }

  async cancel() {
    await this.modalCtrl.dismiss(undefined, null, 'emailModal');
  }

  async send() {
    if (this.form.invalid) {
      return;
    }
    const formValue = this.form.value;
    const contacts = formValue.selectedContacts as { institutionId: string; email: string }[];
    if (formValue.individualMail) {
      for (const individualMail of splitMultipleEmails(formValue.individualMail)) {
        contacts.push({ institutionId: null, email: individualMail });
      }
    }
    await this.modalCtrl.dismiss(
      contacts.map(contact => contact.email),
      null,
      'emailModal'
    );
  }
}
